import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import {
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Radio,
    RadioGroup,
    VStack,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import {
    Category,
    CategoryApi,
    DepartmentApi,
    Region,
    RegionApi,
} from "../../../apis";
import { APPC } from "../../../config";

export type SchoolFilterDataType = {
    school_search: string;
    category: string;
    region: string;
    department: string;
    isActive: boolean;
};

const defaultValues: SchoolFilterDataType = {
    school_search: "",
    category: "",
    region: "",
    department: "",
    isActive: false,
};

export const SchoolFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();

    // state & const
    const [isInit, setIsInit] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [categoryOptions, setCategoryOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedCategory, setSelectedCategory] =
        useState<AppSelectOption | null>(null);
    const [totalCategories, setTotalCategories] = useState(0);
    const [categoryPage, setCategoryPage] = useState(1);
    const [categorySearch, setCategorySearch] = useState("");
    const [loadingRegions, setLoadingRegions] = useState(true);
    const [regions, setRegions] = useState<Region[]>([]);
    const [regionOptions, setRegionOptions] = useState<AppSelectOption[]>([]);
    const [selectedRegion, setSelectedRegion] =
        useState<AppSelectOption | null>(null);
    const [departmentOptions, setDepartmentOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedDepartment, setSelectedDepartment] =
        useState<AppSelectOption | null>(null);

    const { handleSubmit, register, reset, setValue, control } =
        useForm<SchoolFilterDataType>({
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (formData: SchoolFilterDataType) => {
        const filterParams = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] || formData[key] === false) {
                let nKey = key;
                switch (key) {
                    case "category":
                    case "region":
                    case "department":
                        nKey = `${key}.id`;
                        break;
                }
                filterParams[nKey] = formData[key];
            }
        });
        setFilterData(filterParams);
    };

    const loadCategoryOptions = () => {
        setLoadingCategories(true);
        const params = {
            [`order[name]`]: "asc",
            type: APPC.BE.Category.TYPE.TYPE_SCHOOL,
        };

        if (categorySearch) {
            params["name"] = categorySearch;
        }

        CategoryApi.getCollection<Category>(categoryPage, params)
            .then(({ response }) => {
                if (response !== null) {
                    setTotalCategories(response.totalItems);
                    const newOptions =
                        response.items?.map((e) => ({
                            value: CategoryApi.toResourceIRI(e.id),
                            label: e.name,
                        })) || [];
                    categoryPage === 1
                        ? setCategoryOptions(newOptions)
                        : setCategoryOptions((prevOptions) => [
                              ...prevOptions,
                              ...newOptions,
                          ]);
                }
            })
            .finally(() => {
                setLoadingCategories(false);
            });
    };

    const loadRegionOptions = () => {
        setLoadingRegions(true);
        RegionApi.getCollection<Region>(1, {
            [`order[name]`]: "asc",
            pagination: false,
        })
            .then(({ response }) => {
                setRegions(response?.items || []);
                setRegionOptions(
                    response?.items?.map((e) => ({
                        value: RegionApi.toResourceIRI(e.id),
                        label: e.name,
                    })) || []
                );
            })
            .finally(() => setLoadingRegions(false));
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedCategory(null);
        setSelectedRegion(null);
        setSelectedDepartment(null);
        reset(defaultValues);
    };

    // useEffect
    useEffect(() => {
        if (isInit) {
            loadCategoryOptions();
        }
    }, [isInit, categoryPage, categorySearch]);

    useEffect(() => {
        if (isInit) {
            loadRegionOptions();
        }
    }, [isInit]);

    useEffect(() => {
        if (selectedRegion) {
            const currentRegions = regions?.filter(
                (e) => e["@id"] === selectedRegion?.value
            );
            if (currentRegions?.[0]?.departments) {
                setDepartmentOptions(
                    currentRegions?.[0].departments?.map((e) => ({
                        value: DepartmentApi.toResourceIRI(e.id),
                        label: e.name,
                    })) || []
                );
            }
        } else {
            setDepartmentOptions([]);
        }
    }, [selectedRegion]);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            setIsInit={setIsInit}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={4} alignItems={"stretch"} justify={"start"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"school_search"}
                            defaultValue={filterData?.school_search || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.School:category.label")}>
                        <Controller
                            control={control}
                            name="category"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingCategories}
                                    options={categoryOptions}
                                    value={selectedCategory}
                                    placeholder={t(
                                        "ent.School:category.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedCategory(
                                            e as AppSelectOption
                                        );
                                    }}
                                    onMenuScrollToBottom={() => {
                                        if (
                                            categoryOptions.length >=
                                            totalCategories
                                        ) {
                                            return;
                                        }
                                        setCategoryPage(
                                            (prevPage) => prevPage + 1
                                        );
                                    }}
                                    onInputChange={(inputValue: string) => {
                                        if (inputValue !== categorySearch) {
                                            setCategorySearch(inputValue);
                                            if (categoryPage !== 1) {
                                                setCategoryPage(1);
                                            }
                                            setCategoryOptions([]);
                                        }
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.School:region.label")}>
                        <Controller
                            control={control}
                            name="region"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingRegions}
                                    options={regionOptions}
                                    value={selectedRegion || null}
                                    placeholder={t(
                                        "ent.School:region.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedRegion(e as AppSelectOption);
                                        setSelectedDepartment(null);
                                        setValue("department", "");
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.School:department.label")}>
                        <Controller
                            control={control}
                            name="department"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingRegions}
                                    options={departmentOptions}
                                    value={selectedDepartment || null}
                                    placeholder={t(
                                        "ent.School:department.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedDepartment(
                                            e as AppSelectOption
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.School:isActive.label")}>
                        <RadioGroup
                            defaultValue={filterData?.isActive}
                            w={"full"}
                        >
                            <HStack alignItems={"start"} w={"full"}>
                                <Radio {...register("isActive")} value={""}>
                                    {t("cmn:label.none")}
                                </Radio>
                                <Radio {...register("isActive")} value={"true"}>
                                    {t("cmn:label.yes")}
                                </Radio>
                                <Radio
                                    {...register("isActive")}
                                    value={"false"}
                                >
                                    {t("cmn:label.no")}
                                </Radio>
                            </HStack>
                        </RadioGroup>
                    </AppFormControl>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
